import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { formatDate, formatEuro, formatSquareMeters } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import {
  Avatar,
  Banner,
  Button,
  Checkbox,
  Dialog,
  Eyebrow,
  ImageSlot,
  Pill,
  RadioCard,
  SearchField,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '../../ds/components';
import { ApiError, errorMessage } from '../../lib/api';
import { useWorkspace } from '../../lib/session';
import {
  blockedDetailsOf,
  startReservation,
  useAvailableUnits,
  useInvalidateReservations,
  useReservation,
  useReservationContext,
  useUnitDetail,
  type AlternativeUnit,
  type BlockedDetails,
  type ContactHit,
  type ReservationItem,
  type StartReservationBody,
  type StartReservationResponse,
} from './api';
import {
  initialsOf,
  parkingSpaceLine,
  serviceChargeLine,
  unitLine,
  unitStatusLabels,
  unitStatusVariants,
} from './labels';
import { OnSiteSignatureDialog } from './OnSiteSignatureDialog';
import styles from './ReserveUnitDialog.module.css';

export interface ReserveUnitDialogProps {
  open: boolean;
  unitId: string;
  onClose: () => void;
  /** Called once a reservation exists (after the send, the on-site signature or „Fertig"). */
  onReserved: (reservationId: string) => void;
  /** Preselects the contact (the deal record opens the dialog for its contact). */
  contactId?: string | undefined;
}

type SignatureMethod = 'link' | 'on_site';

const UNIT_RESERVED_MESSAGE = 'Die Einheit ist bereits reserviert.';

function useDebounced(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Comparable free units when the context already reports the unit as blocked (nearest price first). */
function comparable(
  rows: Array<{
    id: string;
    number: string;
    floor: string | null;
    rooms: number | null;
    areaSqm: number | null;
    priceCents: number | null;
    status: string;
    availability: string;
    reservation: unknown;
  }>,
  blocked: { id: string; priceCents: number | null },
): AlternativeUnit[] {
  const price = blocked.priceCents ?? 0;
  return rows
    .filter((row) => row.id !== blocked.id && row.availability === 'available' && row.reservation === null)
    .sort(
      (a, b) =>
        Math.abs((a.priceCents ?? 0) - price) - Math.abs((b.priceCents ?? 0) - price) ||
        a.number.localeCompare(b.number, 'de'),
    )
    .slice(0, 3)
    .map((row) => ({
      unitId: row.id,
      number: row.number,
      floor: row.floor,
      areaSqm: row.areaSqm,
      rooms: row.rooms,
      priceCents: row.priceCents,
    }));
}

/**
 * Reservierung 01/02/03 + Objekte 05: contact search with the deal context, the unit's data, the signature
 * method (link / on site), the notes and „Reservierung senden" → `POST /reservations`. Referrers
 * (`mode: 'request'`) choose the advisor in charge instead of the method. A blocked unit renders the
 * alternatives of Reservierung 02; the on-site method continues in the signature dialog.
 */
export function ReserveUnitDialog({ open, unitId, onClose, onReserved, contactId }: ReserveUnitDialogProps) {
  const workspace = useWorkspace();
  const { toast } = useToast();
  const invalidate = useInvalidateReservations();
  const [activeUnitId, setActiveUnitId] = useState(unitId);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState<ContactHit | null>(null);
  const [method, setMethod] = useState<SignatureMethod>('link');
  const [sendAgreement, setSendAgreement] = useState(true);
  const [advisorId, setAdvisorId] = useState('');
  const [message, setMessage] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [blocked, setBlocked] = useState<BlockedDetails | null>(null);
  const [result, setResult] = useState<StartReservationResponse | null>(null);
  const [signing, setSigning] = useState<ReservationItem | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setActiveUnitId(unitId);
    setQ('');
    setPicked(null);
    setMethod('link');
    setSendAgreement(true);
    setAdvisorId('');
    setMessage('');
    setPending(false);
    setError(null);
    setFieldErrors({});
    setBlocked(null);
    setResult(null);
    setSigning(null);
    setCopied(false);
  }, [open, unitId]);

  const debouncedQ = useDebounced(q.trim(), 250);
  const preselectedId = contactId ?? null;
  const context = useReservationContext(activeUnitId, debouncedQ, preselectedId, open);
  const unitDetail = useUnitDetail(activeUnitId, open);
  const data = context.data;
  const contextBlocked = data !== undefined && !data.canReserve;
  const alternativesQuery = useAvailableUnits(
    data?.unit.propertyId ?? null,
    open && contextBlocked && blocked === null,
  );
  const liveReservation = useReservation(
    open && contextBlocked && blocked === null ? (data?.reservationId ?? null) : null,
  );

  useEffect(() => {
    if (data?.advisors.length && advisorId === '') {
      const mine = data.advisors.find((advisor) => advisor.id === workspace.user.id);
      setAdvisorId((mine ?? data.advisors[0])?.id ?? '');
    }
  }, [data, advisorId, workspace.user.id]);

  const selected: ContactHit | null = picked ?? (preselectedId ? (data?.selected ?? null) : null);
  /**
   * Opened from the deal with its contact (Reservierung 01/03): no contact search, no „Angaben aus der
   * Einheit“ box and no e-mail checkbox — those belong to the unit record's „Kontakt zuordnen“ (Objekte 05).
   */
  const fromDeal = preselectedId !== null && selected !== null;
  const requestMode = data?.mode === 'request';
  const approver = workspace.approvalRecipientName ?? 'Admin';
  const advisorName = requestMode
    ? (data?.advisors.find((advisor) => advisor.id === advisorId)?.name ?? 'Der Betreuer')
    : null;

  const hits = useMemo(() => {
    const list = data?.hits ?? [];
    if (selected && !list.some((hit) => hit.contactId === selected.contactId)) return [selected, ...list];
    return list;
  }, [data?.hits, selected]);

  const unitRowLine = unitLine({
    areaSqm: data?.unit.areaSqm ?? null,
    rooms: unitDetail.data?.data.rooms ?? null,
    floor: unitDetail.data?.data.buildingFloor ?? null,
    priceCents: data?.unit.priceCents ?? null,
  });

  const blockedView: BlockedDetails | null =
    blocked ??
    (contextBlocked && data
      ? {
          alternatives: comparable(alternativesQuery.data?.items ?? [], data.unit),
          blocking: liveReservation.data
            ? {
                reservationId: liveReservation.data.id,
                status: liveReservation.data.status,
                since: liveReservation.data.startedAt,
                advisorShort: liveReservation.data.advisor?.name ?? liveReservation.data.startedBy.name,
                approvedByShort: liveReservation.data.approval?.byShort ?? null,
              }
            : null,
        }
      : null);

  function switchUnit(alternative: AlternativeUnit) {
    setActiveUnitId(alternative.unitId);
    setBlocked(null);
    setError(null);
  }

  async function submit() {
    if (!data || !selected) return;
    if (requestMode && advisorId === '') {
      setFieldErrors({ advisorId: 'Bitte wählen Sie einen Betreuer.' });
      return;
    }
    setPending(true);
    setError(null);
    setFieldErrors({});
    const body: StartReservationBody = {
      unitId: data.unit.id,
      contactId: selected.contactId,
      signatureMethod: requestMode ? 'link' : method,
    };
    if (!requestMode && method === 'link') body.sendAgreement = sendAgreement;
    if (requestMode) {
      body.advisorId = advisorId;
      if (message.trim()) body.messageToAdvisor = message.trim();
    }
    try {
      const response = await startReservation(body);
      await invalidate();
      if (!requestMode && method === 'on_site') {
        setSigning(response);
        return;
      }
      setResult(response);
    } catch (cause) {
      const details = blockedDetailsOf(cause);
      if (details) {
        setBlocked(details);
      } else if (cause instanceof ApiError && Object.keys(cause.fields).length > 0) {
        setFieldErrors(cause.fields);
      } else {
        setError(errorMessage(cause));
      }
    } finally {
      setPending(false);
    }
  }

  async function copyLink() {
    if (!result?.link) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
    } catch {
      setError(
        'Der Link konnte nicht in die Zwischenablage kopiert werden — markieren Sie ihn und kopieren Sie ihn von Hand.',
      );
    }
  }

  function finish(reservationId: string) {
    onReserved(reservationId);
  }

  // --- on-site signature (after the 201) ---------------------------------------------------------
  if (signing) {
    return (
      <OnSiteSignatureDialog
        key={signing.id}
        open={open}
        reservation={signing}
        onClose={() => {
          toast({ message: `Reservierung ${signing.unit.number} gestartet · Unterschrift offen` });
          finish(signing.id);
        }}
        onSigned={(item) => {
          toast({ message: `${item.unit.number} unterschrieben · Freigabe durch ${approver} angefragt` });
          finish(item.id);
        }}
      />
    );
  }

  // --- success (link sent / request placed) ------------------------------------------------------
  if (result) {
    const contactName = result.contact.name;
    return (
      <Dialog
        open={open}
        onClose={() => finish(result.id)}
        width={600}
        eyebrow="Reservierung starten"
        title={
          requestMode
            ? 'Reservierung angestoßen'
            : result.status === 'awaiting_signature'
              ? `Reservierung ${result.unit.number} gestartet`
              : `${result.unit.number} ist für ${contactName} reserviert`
        }
        footer={<Button onClick={() => finish(result.id)}>Fertig</Button>}
      >
        <div className={styles.stack}>
          {error ? <Banner tone="danger">{error}</Banner> : null}
          <Banner tone="success" title={result.statusLabel}>
            {requestMode
              ? `${result.advisor?.name ?? 'Der Betreuer'} wurde informiert. ${contactName} erhält den Link zur Unterschrift per E-Mail und WhatsApp.`
              : result.link
                ? `Die Reservierungsvereinbarung geht an ${result.contact.email ?? contactName} · Link gültig bis ${result.linkValidUntil ? formatDate(result.linkValidUntil) : '—'}.`
                : `Die Einheit ist ab sofort für alle blockiert. Der Link kann mit „Erneut senden“ an ${contactName} gehen.`}
          </Banner>
          {result.link ? (
            <div className={styles.linkBox}>
              <span className={styles.linkText}>{result.link}</span>
              <Button variant="secondary" size="small" onClick={() => void copyLink()}>
                {copied ? 'Kopiert' : 'Link kopieren'}
              </Button>
            </div>
          ) : null}
          <p className={styles.footnoteText}>
            Der Link wird nur einmal angezeigt. {approver} gibt die Reservierung nach der Unterschrift frei.
          </p>
        </div>
      </Dialog>
    );
  }

  // --- blocked (Reservierung 02) -----------------------------------------------------------------
  if (blockedView && data) {
    const blocking = blockedView.blocking;
    const reason = data.blockedReason ?? UNIT_RESERVED_MESSAGE;
    const reservedByOther = reason === UNIT_RESERVED_MESSAGE;
    return (
      <Dialog
        open={open}
        onClose={onClose}
        width={580}
        eyebrow="Reservierung starten"
        title={
          reservedByOther
            ? `${data.unit.number} ist bereits reserviert`
            : `${data.unit.number} ist nicht verfügbar`
        }
        className={styles.dialog}
        bodyClassName={styles.dialogBody}
        footerNote="Keine Warteliste"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Schließen
            </Button>
            <Button disabled>Reservierung starten</Button>
          </>
        }
      >
        <div className={styles.stack}>
          <UnitRow
            number={data.unit.number}
            line={unitRowLine}
            status={reservedByOther ? 'reserved' : data.unit.status}
          />
          <div className={styles.warnBox} role="status">
            <span className={styles.warnTitle}>
              {reservedByOther ? 'Reserviert für einen anderen Deal' : reason}
            </span>
            {blocking ? (
              <span className={styles.warnText}>
                Seit {formatDate(blocking.since)}
                {blocking.advisorShort ? ` · Betreuer ${blocking.advisorShort}` : ''}
                {blocking.approvedByShort ? ` · freigegeben von ${blocking.approvedByShort}` : ''}
              </span>
            ) : reservedByOther ? (
              <span className={styles.warnText}>
                Die laufende Reservierung liegt außerhalb Ihres Bereichs.
              </span>
            ) : null}
          </div>
          <div className={styles.sectionLabel}>Vergleichbare freie Einheiten</div>
          {blockedView.alternatives.length === 0 ? (
            <p className={styles.footnoteText}>
              {alternativesQuery.isPending && blocked === null
                ? 'Freie Einheiten werden geladen …'
                : 'In diesem Objekt ist derzeit keine vergleichbare Einheit frei.'}
            </p>
          ) : (
            <div className={styles.alternatives}>
              {blockedView.alternatives.map((alternative) => (
                <div key={alternative.unitId} className={styles.alternative}>
                  <span className={styles.alternativeText}>
                    <span className={styles.alternativeNumber}>{alternative.number}</span>
                    <span className={styles.alternativeLine}>{unitLine(alternative)}</span>
                  </span>
                  <Pill variant="neutral">Frei</Pill>
                  <Button variant="secondary" size="small" onClick={() => switchUnit(alternative)}>
                    Stattdessen reservieren
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Dialog>
    );
  }

  // --- start (Reservierung 01/03, Objekte 05) ----------------------------------------------------
  const title = !data
    ? 'Reservierung starten'
    : selected
      ? `${data.unit.number} für ${selected.name} reservieren`
      : 'Kontakt zuordnen';
  const subtitle =
    !data || !selected
      ? undefined
      : requestMode
        ? `${selected.name} unterschreibt selbst. ${advisorName ?? 'Der Betreuer'} wird als Betreuer sofort informiert, freigeben muss ${approver}.`
        : `${selected.name} unterschreibt die Reservierung selbst. Danach gibt ${approver} sie frei.`;
  const primaryLabel = requestMode
    ? 'Reservierung anstoßen'
    : method === 'link' && !fromDeal
      ? 'Reservierung senden'
      : 'Reservierung starten';
  const canSubmit = Boolean(data && selected && data.canReserve && !pending);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={fromDeal ? 580 : 600}
      eyebrow={
        data && !selected
          ? `Reservierung · ${data.unit.number} · ${data.unit.propertyName}`
          : 'Reservierung starten'
      }
      title={title}
      subtitle={subtitle}
      className={styles.dialog}
      bodyClassName={styles.dialogBody}
      footerNote={
        selected && !fromDeal
          ? 'Deal wird mit der Einheit verknüpft und nach der Freigabe auf Reserviert gesetzt.'
          : undefined
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} disabled={!canSubmit} onClick={() => void submit()}>
            {primaryLabel}
          </Button>
        </>
      }
    >
      <div className={styles.stack}>
        {context.isError ? <Banner tone="danger">{errorMessage(context.error)}</Banner> : null}
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {context.isPending && !data ? (
          <div className={styles.stack} aria-busy="true">
            <Skeleton height={40} />
            <Skeleton height={82} />
            <Skeleton height={92} />
          </div>
        ) : null}
        {data ? (
          <>
            {fromDeal ? null : (
              <>
                <SearchField
                  label="Kontakt suchen"
                  placeholder="Name, E-Mail oder Deal suchen …"
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                  autoFocus
                />
                {hits.length > 0 ? (
                  <div className={styles.hits} role="listbox" aria-label="Treffer">
                    {hits.map((hit) => {
                      const active = selected?.contactId === hit.contactId;
                      return (
                        <button
                          key={hit.contactId}
                          type="button"
                          role="option"
                          aria-selected={active}
                          className={clsx(styles.hit, active && styles.hitSelected)}
                          onClick={() => {
                            setPicked(hit);
                            setError(null);
                          }}
                        >
                          <Avatar
                            initials={initialsOf(hit.name)}
                            name={hit.name}
                            size={32}
                            tone={active ? 'accent' : 'sand'}
                          />
                          <span className={styles.hitText}>
                            <span className={styles.hitName}>{hit.name}</span>
                            <span className={styles.hitMeta}>
                              {[
                                hit.email,
                                hit.dealLabel,
                                hit.selfDisclosure.label !== '—'
                                  ? `Selbstauskunft ${hit.selfDisclosure.label}`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </span>
                          </span>
                          {active ? <span className={styles.hitTag}>Ausgewählt</span> : null}
                        </button>
                      );
                    })}
                  </div>
                ) : debouncedQ !== '' && !context.isFetching ? (
                  <p className={styles.footnoteText}>Kein Kontakt gefunden.</p>
                ) : null}
                <Link to="/contacts/new" className={styles.newContact}>
                  Neuen Kontakt anlegen
                </Link>
              </>
            )}

            <UnitRow number={data.unit.number} line={unitRowLine} status={data.unit.status} />

            {fromDeal ? null : (
              <div className={styles.unitBox}>
                <Eyebrow as="div">Angaben aus der Einheit</Eyebrow>
                <dl className={styles.unitGrid}>
                  <div className={styles.unitCell}>
                    <dt>Einheit</dt>
                    <dd>
                      {data.unit.number}
                      {data.unit.areaSqm !== null ? ` · ${formatSquareMeters(data.unit.areaSqm)}` : ''}
                    </dd>
                  </div>
                  <div className={styles.unitCell}>
                    <dt>Kaufpreis</dt>
                    <dd>{data.unit.priceCents !== null ? formatEuro(data.unit.priceCents) : '—'}</dd>
                  </div>
                  <div className={styles.unitCell}>
                    <dt>Stellplatz</dt>
                    <dd>{parkingSpaceLine(data.unit.parkingSpace)}</dd>
                  </div>
                  <div className={styles.unitCell}>
                    <dt>Hausgeld</dt>
                    <dd>{serviceChargeLine(data.unit.serviceChargeCents)}</dd>
                  </div>
                </dl>
              </div>
            )}

            {!data.canReserve && data.blockedReason ? (
              <Banner tone="warning">{data.blockedReason}</Banner>
            ) : null}

            {requestMode ? (
              <>
                <Select
                  label="Betreuer"
                  required
                  value={advisorId}
                  onChange={(event) => setAdvisorId(event.target.value)}
                  options={data.advisors.map((advisor) => ({
                    value: advisor.id,
                    label: `${advisor.name} · Betreuer`,
                  }))}
                  placeholder={data.advisors.length === 0 ? 'Kein Berater mit Reservierungsrecht' : undefined}
                  error={fieldErrors['advisorId']}
                />
                <Textarea
                  label="Nachricht (optional)"
                  rows={3}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  error={fieldErrors['messageToAdvisor']}
                />
                <div className={styles.notes}>
                  <span>Die Einheit ist ab sofort für alle blockiert.</span>
                  <span>{advisorName} erhält eine Benachrichtigung und sieht die Reservierung im Deal.</span>
                </div>
              </>
            ) : (
              <>
                <div className={styles.fieldLabel}>Unterschrift</div>
                <div className={styles.methods} role="radiogroup" aria-label="Unterschrift">
                  <RadioCard
                    name="signature-method"
                    value="link"
                    checked={method === 'link'}
                    onChange={() => setMethod('link')}
                    title="Link an Kunden senden"
                    description="E-Mail und WhatsApp"
                  />
                  <RadioCard
                    name="signature-method"
                    value="on_site"
                    checked={method === 'on_site'}
                    onChange={() => setMethod('on_site')}
                    title="Jetzt vor Ort unterschreiben"
                    description="auf diesem Gerät"
                  />
                </div>
                {method === 'link' && !fromDeal ? (
                  <Checkbox
                    label="Reservierungsvereinbarung per E-Mail zur digitalen Unterschrift senden. Objekt- und Einheitenunterlagen gehen mit."
                    checked={sendAgreement}
                    onChange={(event) => setSendAgreement(event.target.checked)}
                  />
                ) : null}
                {fieldErrors['signatureMethod'] ? (
                  <div className={styles.fieldError} role="alert">
                    {fieldErrors['signatureMethod']}
                  </div>
                ) : null}
                <div className={styles.notes}>
                  <span>Die Einheit ist ab sofort für alle blockiert. Es gibt keine Warteliste.</span>
                  <span>Die Reservierung hat keine Frist und gilt, bis sie aufgehoben wird.</span>
                  <span>Freigabe durch {approver} · Task wird nach der Unterschrift angelegt.</span>
                </div>
              </>
            )}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

function UnitRow({
  number,
  line,
  status,
}: {
  number: string;
  line: string;
  status: keyof typeof unitStatusLabels;
}) {
  return (
    <div className={styles.unitRow}>
      <ImageSlot
        alt={`Grundriss ${number}`}
        kind="plan"
        height={52}
        radius="none"
        className={styles.unitImage}
      />
      <span className={styles.unitText}>
        <span className={styles.unitNumber}>{number}</span>
        {line ? <span className={styles.unitLine}>{line}</span> : null}
      </span>
      <Pill variant={unitStatusVariants[status]}>{unitStatusLabels[status]}</Pill>
    </div>
  );
}
